import { createContext, ReactNode, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import api from "@helpers/api";
import { useKeycloak } from "@react-keycloak/web";
import { AllowedComponent } from "./AllowedComponent";

import { Routes as ACCRoutes } from "./Routes";
import { RouteType } from "./Routes/types";

export type DashboardContextType = {
  value: string;
  handleModuleSelection: (module: string) => void;
};

export const DashboardContext = createContext<DashboardContextType | null>(
  null,
);

export const AppRouter = () => {
  const [currentModule, setCurrenModule] = useState("HRM");

  const buildRoute = (route: RouteType): ReactNode | null => {
    if (route === undefined || null) return null;

    const {
      id,
      children,
      path,
      element,
      redirection,
      permissions,
      index: idx,
    } = route;

    return idx ? (
      <Route
        index
        key={id}
        element={AllowedComponent(element, redirection, permissions)}
      />
    ) : (
      <Route
        path={path}
        key={id}
        element={AllowedComponent(element, redirection, permissions)}
      >
        {children ? (
          children.length && children.map((child) => buildRoute(child))
        ) : (
          <></>
        )}
      </Route>
    );
  };

  const { keycloak } = useKeycloak();

  const handleModuleSelection = (module: string) => {
    setCurrenModule(module);
  };

  useEffect(() => {
    api.interceptors.request.use(
      (config) => {
        config.headers.Authorization = `Bearer ${keycloak.token}`;

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }, [keycloak.token]);

  useEffect(() => {
    const moduleName = localStorage.getItem("module");
    if (moduleName) {
      setCurrenModule(moduleName);
    }
  }, []);

  useEffect(() => {
    if (currentModule) {
      localStorage.setItem("module", currentModule);
    }
  }, [currentModule]);

  // logout if the user is inactive
  keycloak.onAuthRefreshError = () => {
    keycloak.logout({
      redirectUri: process.env.REACT_APP_KEYCLOAK_LOGOUT_REDIRECT_URI,
    });
  };

  return (
    <DashboardContext.Provider
      value={{ value: currentModule, handleModuleSelection }}
    >
      <Routes>{ACCRoutes.map((route: RouteType) => buildRoute(route))}</Routes>
    </DashboardContext.Provider>
  );
};
